import { idbDelete, idbGet, idbGetAll, idbPut, STORES } from './database';
import type { ConversationTurn, SentFileRecord } from '../pipeline/types';

export interface ConversationRecord {
  id: string;
  title: string;
  turns: ConversationTurn[];
  /** Per-file {hash, content} of the last version actually sent to Claude in this conversation — C1 diffs against this. */
  sentFiles: Record<string, SentFileRecord>;
  /** D1's LLM-generated rolling summary, and how many of `turns` it currently covers — only set/used in LLM-assisted mode. */
  historySummary?: string;
  historySummarizedThroughCount?: number;
  /**
   * ContextTrees project this conversation is scoped to, if any. Optional and additive: records
   * written before this field existed simply don't have it, and nothing backfills them — IndexedDB
   * is the only copy a local-only user has, so we never rewrite their history to add a field.
   */
  projectId?: string;
  createdAt: number;
  updatedAt: number;
}

export function newConversation(title = 'New conversation', projectId?: string): ConversationRecord {
  const now = Date.now();
  return { id: crypto.randomUUID(), title, turns: [], sentFiles: {}, projectId, createdAt: now, updatedAt: now };
}

export async function saveConversation(record: ConversationRecord): Promise<void> {
  await idbPut(STORES.conversations, record);
}

export async function getConversation(id: string): Promise<ConversationRecord | undefined> {
  return idbGet<ConversationRecord>(STORES.conversations, id);
}

export async function getAllConversations(): Promise<ConversationRecord[]> {
  const all = await idbGetAll<ConversationRecord>(STORES.conversations);
  return all.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteConversation(id: string): Promise<void> {
  await idbDelete(STORES.conversations, id);
}
